"use client";

import Button from "@/components/ui/Button";
import Container from "@/components/ui/Container";
import ScrollReveal from "@/components/ui/ScrollReveal";
import Section from "@/components/ui/Section";
import SectionHeader from "@/components/ui/SectionHeader";
import { useState } from "react";
import { FaPlus, FaSpinner, FaCircleCheck, FaCircleExclamation } from "react-icons/fa6";
import { FormInput, FormTextarea } from "./FormFields";
import { cn } from "@/lib/utils";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import { ContactSubmissionResponse } from "@/types";

interface ContactFormSectionProps {
    id?: string;
    className?: string;
    source?: string;
    title?: string;
}

type FormStatus = "idle" | "submitting" | "success" | "error";

const INITIAL_FORM_DATA = {
    fullName: "",
    phone: "",
    email: "",
    query: "",
    _honey: "",
};

export default function ContactFormSection({
    id = "contact-form",
    className,
    source = "Contact",
    title = "Get In Touch",
}: ContactFormSectionProps = {}) {
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [status, setStatus] = useState<FormStatus>("idle");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const { executeRecaptcha, loadRecaptcha, siteKey } = useRecaptcha();

    const isSubmitting = status === "submitting";

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (status === "error") {
            setStatus("idle");
            setErrorMessage("");
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const trimmedName = formData.fullName.trim();
        if (!trimmedName || trimmedName.length < 2) {
            setStatus("error");
            setErrorMessage("Please enter your full name.");
            return;
        }

        const nameRegex = /^[a-zA-Z\s]+$/;
        if (!nameRegex.test(trimmedName)) {
            setStatus("error");
            setErrorMessage("Full name can only contain letters and spaces (no numbers or special characters).");
            return;
        }

        const trimmedPhone = formData.phone.trim();
        const phoneRegex = /^\+?[0-9]{10,15}$/;
        if (!trimmedPhone || !phoneRegex.test(trimmedPhone)) {
            setStatus("error");
            setErrorMessage("Please enter a valid phone number (10 to 15 digits, optionally prefixed with '+').");
            return;
        }

        const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const trimmedEmail = formData.email.trim();
        if (!trimmedEmail) {
            setStatus("error");
            setErrorMessage("Please enter your email address.");
            return;
        }
        if (!EMAIL_REGEX.test(trimmedEmail) || trimmedEmail.length > 254) {
            setStatus("error");
            setErrorMessage("Please enter a valid email address.");
            return;
        }

        const trimmedQuery = formData.query.trim();
        if (!trimmedQuery) {
            setStatus("error");
            setErrorMessage("Please enter your query.");
            return;
        }
        if (trimmedQuery.length > 2000) {
            setStatus("error");
            setErrorMessage("Query must not exceed 2000 characters.");
            return;
        }

        setStatus("submitting");
        setErrorMessage("");

        try {
            let recaptchaToken: string | null = null;
            if (siteKey) {
                try {
                    recaptchaToken = await executeRecaptcha("submit_contact");
                } catch (recaptchaErr) {
                    console.error("[reCAPTCHA] Failed to obtain token:", recaptchaErr);
                }
            }

            const response = await fetch("/api/contact", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    ...formData,
                    source,
                    recaptchaToken: recaptchaToken || undefined,
                }),
            });

            const result: ContactSubmissionResponse = await response.json().catch(() => ({
                success: false,
                error: "Unable to parse server response.",
            }));

            if (!response.ok || !result.success) {
                setStatus("error");
                setErrorMessage(
                    result.error || "Something went wrong while sending your message. Please try again."
                );
                return;
            }

            setStatus("success");
            setFormData(INITIAL_FORM_DATA);
        } catch {
            setStatus("error");
            setErrorMessage(
                "Unable to connect to the server. Please check your internet connection and try again."
            );
        }
    };

    const handleReset = () => {
        setStatus("idle");
        setErrorMessage("");
        setFormData(INITIAL_FORM_DATA);
    };

    return (
        <Section id={id} className={cn("pb-10 scroll-mt-20 sm:scroll-mt-28", className)}>
            <Container>
                <ScrollReveal variant="fade-up" duration={700}>
                    {title && <SectionHeader title={title} className="mb-6 sm:mb-8" />}
                    <div className="max-w-xl mx-auto w-full">
                        {status === "success" ? (
                            <div className="border border-brand-green/60 bg-brand-green/10 p-8 text-center rounded-none backdrop-blur-sm">
                                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-green/20 text-brand-green mb-4">
                                    <FaCircleCheck className="w-7 h-7" />
                                </div>
                                <h3 className="text-xl md:text-2xl font-oswald text-white font-medium uppercase tracking-wider mb-2">
                                    Inquiry Sent Successfully!
                                </h3>
                                <p className="text-cream/80 text-sm font-poppins max-w-md mx-auto mb-6">
                                    Thank you for getting in touch with Purple Octopus. Our team has received your message and will reach out to you shortly.
                                </p>
                                <Button
                                    type="button"
                                    onClick={handleReset}
                                    variant="outline"
                                    outlineColor="green"
                                    size="md"
                                    icon={<FaPlus className="w-3.5 h-3.5" />}
                                    iconColor="green"
                                >
                                    Send Another Message
                                </Button>
                            </div>
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                onFocusCapture={loadRecaptcha}
                                onPointerDownCapture={loadRecaptcha}
                                className="flex flex-col gap-5"
                            >
                                {/* Hidden honeypot field to block automated bot spam */}
                                <input
                                    type="text"
                                    name="_honey"
                                    value={formData._honey}
                                    onChange={handleChange}
                                    style={{ display: "none" }}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                />

                                <div>
                                    <FormInput
                                        type="text"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        placeholder="Full Name"
                                        required
                                        pattern="[a-zA-Z\s]+"
                                        title="Letters and spaces only (no numbers or special characters)"
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div>
                                    <FormInput
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="Phone Number"
                                        required
                                        pattern="^\+?[0-9]{10,15}$"
                                        title="Please enter a valid phone number (10 to 15 digits, optionally prefixed with '+')"
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div>
                                    <FormInput
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="E-mail ID"
                                        required
                                        disabled={isSubmitting}
                                    />
                                </div>

                                <div>
                                    <FormTextarea
                                        name="query"
                                        value={formData.query}
                                        onChange={handleChange}
                                        placeholder="Type Your Query"
                                        rows={4}
                                        required
                                        disabled={isSubmitting}
                                    />
                                </div>

                                {status === "error" && errorMessage && (
                                    <div
                                        role="alert"
                                        className="flex items-start gap-3 p-4 border border-red-500/50 bg-red-950/30 text-red-200 text-sm font-poppins"
                                    >
                                        <FaCircleExclamation className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                                        <span>{errorMessage}</span>
                                    </div>
                                )}

                                <div className="pt-2 flex flex-col items-center justify-center">
                                    <Button
                                        type="submit"
                                        variant="outline"
                                        size="md"
                                        disabled={isSubmitting}
                                        icon={
                                            isSubmitting ? (
                                                <FaSpinner className="w-3.5 h-3.5 animate-spin" />
                                            ) : (
                                                <FaPlus className="w-3.5 h-3.5" />
                                            )
                                        }
                                    >
                                        {isSubmitting ? "Submitting..." : "Submit"}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </div>
                </ScrollReveal>
            </Container>
        </Section>
    );
}
